import { ITINERARY_TIME_SLOTS, ITINERARY_TRANSPORT_LABELS } from "../config";
import type { ItineraryDay, ItineraryNode, ItinerarySegment, TimeSlot } from "../types";
import type { ItineraryTableSource } from "../types/itineraryTable";
import { formatDayWeekday, isPendingTransportSource, resolveDayEndpointNodes, summarizeDayEndpoints } from "./itineraryEditorService";
import { effectiveSummaryText, hasTextContent } from "./itinerarySummaryService";
import { isStructuralDayContinuity } from "./itineraryOutputService";

const slotLabels: Record<TimeSlot, string> = { morning: "上午", afternoon: "下午", evening: "晚上" };
const summaryFields = { morning: "morningSummary", afternoon: "afternoonSummary", evening: "eveningSummary" } as const;

/** 日期按 UTC 日历递推，显式但无效的日期不冒充正常日期。 */
function tableDate(editor: ItineraryTableSource, day: ItineraryDay) {
  const base = day.dayDate || editor.startDate;
  if (!formatDayWeekday(base, 1)) return { dateLabel: "日期待定", weekday: "" };
  const date = new Date(`${base}T00:00:00Z`);
  if (!day.dayDate) date.setUTCDate(date.getUTCDate() + day.dayNumber - 1);
  return {
    dateLabel: `${date.getUTCMonth() + 1} 月 ${date.getUTCDate()} 日`,
    weekday: formatDayWeekday(date.toISOString().slice(0, 10), 1),
  };
}

/** 只展示实际交通事实，缺段与待确认段保持可见。 */
function tableTransport(segment?: ItinerarySegment): string {
  if (!segment) return "待确认";
  const label = ITINERARY_TRANSPORT_LABELS[segment.transportMode];
  return [label, isPendingTransportSource(segment.sourceType) ? "待确认" : "", segment.startDayId !== segment.endDayId ? "跨日" : ""].filter(Boolean).join("·");
}

/** 从当前编辑草稿整理行程表格，保持原始业务图不变。 */
export function buildItineraryTableDays(editor: ItineraryTableSource) {
  const nodesById = new Map(editor.days.flatMap((day) => day.nodes).map((node) => [node.id, node]));
  const daysById = new Map(editor.days.map((day) => [day.id, day.dayNumber]));
  const segmentsById = new Map(editor.segments.map((segment) => [segment.id, segment]));
  return [...editor.days].sort((a, b) => a.dayNumber - b.dayNumber).map((day) => {
    const orderedNodes = resolveDayEndpointNodes(day);
    const tracks = day.parallelTracks?.tracks.length ? day.parallelTracks.tracks.map((track) => ({
      id: track.id, title: track.title,
      nodes: track.nodeIds.map((id) => nodesById.get(id)).filter((node): node is ItineraryNode => Boolean(node)),
      segments: track.segmentIds.map((id) => segmentsById.get(id)).filter((segment): segment is ItinerarySegment => Boolean(segment)),
    })) : [{ id: day.id, title: "", nodes: orderedNodes, segments: editor.segments.filter((segment) => segment.startDayId === day.id && segment.endDayId === day.id) }];
    const paths = tracks.filter((track) => track.nodes.length).map((track) => ({
      id: track.id, title: track.title,
      stops: track.nodes.map((node, index) => ({
        title: node.title.trim() || "未命名点位",
        ...(index ? { transport: tableTransport(track.segments.find((segment) => segment.fromNodeId === track.nodes[index - 1].id && segment.toNodeId === node.id)) } : {}),
      })),
    }));
    const crossing = editor.segments.filter((segment) => {
      if (segment.startDayId === segment.endDayId) return false;
      const start = daysById.get(segment.startDayId);
      const end = daysById.get(segment.endDayId);
      const from = nodesById.get(segment.fromNodeId);
      const to = nodesById.get(segment.toNodeId);
      if (start === undefined || end === undefined || !from || !to) return false;
      if (isStructuralDayContinuity(segment, nodesById)) return false;
      return start <= day.dayNumber && day.dayNumber <= end;
    });
    crossing.forEach((segment) => paths.push({
      id: segment.id, title: "跨日交通",
      stops: [{ title: nodesById.get(segment.fromNodeId)!.title }, { title: nodesById.get(segment.toNodeId)!.title, transport: tableTransport(segment) }],
    }));
    const inTransit = !day.nodes.length && crossing.some((segment) => daysById.get(segment.startDayId)! < day.dayNumber && day.dayNumber < daysById.get(segment.endDayId)!);
    const slots = ITINERARY_TIME_SLOTS.map((slot) => {
      const groups = tracks.flatMap((track) => {
        const points = track.nodes.filter((node) => node.timeSlot === slot && (node.nodeType === "point" || node.nodeType === "custom"));
        return points.length ? [{ label: track.title, text: points.map((node) => node.title.trim() || "未命名点位").join(" → ") }] : [];
      });
      const summary = day[summaryFields[slot]];
      if (!day.isFullDay && !day.nodes.length && !groups.length && hasTextContent(summary)) groups.push({ label: "", text: summary!.trim() });
      if (inTransit && !groups.length && slot !== "evening") groups.push({ label: "", text: "全天在途" });
      return { key: slot, label: slotLabels[slot], groups };
    }).filter((slot) => slot.key !== "evening" || slot.groups.length);
    const fallbackSummary = day.isFullDay ? effectiveSummaryText(day, "full_day") : day.summary;
    if (!day.nodes.length && hasTextContent(fallbackSummary) && slots.every((slot) => !slot.groups.length)) {
      slots.push({ key: "morning", label: day.isFullDay ? "全天" : "本日", groups: [{ label: "", text: fallbackSummary!.trim() }] });
    }
    const lastNode = orderedNodes.at(-1);
    const stayLabel = day.stayAreaName?.trim() || (lastNode?.nodeType === "stay_area" ? lastNode.title.trim() : "");
    const title = day.dayTitle?.trim() || "";
    const routeTitle = /^(?:第\s*\d+\s*天|D(?:ay)?\s*\d+|待定)$/i.test(title) ? "" : title;
    return {
      id: day.id, dayNumber: day.dayNumber, ...tableDate(editor, day),
      routeLabel: routeTitle || summarizeDayEndpoints(orderedNodes)?.label || "",
      stayLabel,
      unfinished: !day.nodes.length && !crossing.length && !stayLabel && slots.every((slot) => !slot.groups.length),
      slots, paths,
    };
  });
}
